<template>
  <b-row>
    <b-col class="loads-table" cols="12">
      <div class="loads-table__title-container">
        <span>{{ $t('delivery.load') }}</span>
        <div class="loads-table__actions-container">
          <McButton
            v-if="selectedIdxLength > 0"
            variant="secondary-red"
            icon="icon-outline_trash"
            :text="$t('global.delete')"
            @click="handleDelete"
          />
          <McButton :text="$t('global.addField')" icon="icon-fill_plus" @click="handleAdd" />
        </div>
      </div>

      <div class="table-responsive border border-1">
        <b-table-simple class="editable-table">
          <b-thead>
            <b-tr>
              <b-th v-for="field in computedFields" :key="field.name" :style="field.thStyle">
                <div v-if="field.name == 'checkbox'" class="checkbox-cell">
                  <McCheckbox
                    v-model="selectedAll"
                    :disabled="computedLoads.length == 0"
                    @change="handleSelectAll"
                  />
                </div>
                <div v-else>{{ field.label }}</div>
              </b-th>
            </b-tr>
          </b-thead>

          <b-tbody>
            <template v-if="computedLoads.length == 0">
              <b-tr>
                <b-td :colspan="computedFields.length">
                  <div class="text-center my-4">
                    {{ $t('global.text.noData') }}
                  </div>
                </b-td>
              </b-tr>
            </template>

            <template v-else>
              <b-tr v-for="(item, index) in items" :key="index">
                <b-td v-for="field in computedFields" :key="field.name" :style="field?.tdStyle">
                  <div v-if="field.name == 'checkbox'" class="checkbox-cell">
                    <McCheckbox
                      v-model="selectedIdx[index]"
                      @change="$set(selectedIdx, index, $event)"
                    />
                  </div>

                  <ProductValueHelp
                    v-else-if="field.name == 'product_id'"
                    :id="id"
                    :index="index"
                    class="td__overflow-ellipsis"
                    :field="field"
                    :value="computedLoads[index]?.[field.name]"
                    :fullObject="computedObject?.[index]?.[field.name]"
                    @input="
                      handleUpdateFormValues({
                        key: field.name,
                        index,
                        value: $event,
                      })
                    "
                    @update:fullObject="
                      handleUpdateFullObjects({
                        key: field.name,
                        index,
                        value: $event,
                      })
                    "
                  />

                  <div v-else-if="field.name == 'product_type'" class="td__overflow-ellipsis">
                    {{ getProductType({ index, key: 'product_type.name' }) }}
                  </div>

                  <div v-else-if="field.name == 'quantity'">
                    <QuantityInput
                      :id="id"
                      :index="index"
                      :field="field"
                      :value="computedLoads[index]?.[field.name]"
                      @input="
                        handleUpdateFormValues({
                          key: field.name,
                          index,
                          value: $event,
                        })
                      "
                    />
                  </div>

                  <UnitSelectResourceful
                    v-else-if="field.name == 'uom'"
                    :id="id"
                    class="td__overflow-ellipsis"
                    :field="field"
                    :index="index"
                    :productId="computedLoads[index]?.product_id || null"
                    :value="computedLoads[index]?.[field.name]"
                    :fullObject="computedObject?.[index]?.[field.name]"
                    @input="
                      handleUpdateFormValues({
                        key: field.name,
                        index,
                        value: $event,
                      })
                    "
                    @update:fullObject="
                      handleUpdateFullObjects({
                        key: field.name,
                        index,
                        value: $event,
                      })
                    "
                  />

                  <div v-else-if="field.name == 'total_weight'">{{ getWeight({ index }) }} Kg</div>

                  <div v-else-if="field.name == 'total_volume'">{{ getVolume({ index }) }} Cbm</div>

                  <div v-else-if="field.name == 'min_temperature'">
                    {{
                      field.formatter(
                        computedObject[index]?.product_id?.product_type?.product_category
                          ?.min_temperature
                      )
                    }}
                  </div>

                  <div v-else-if="field.name == 'max_temperature'">
                    {{
                      field.formatter(
                        computedObject[index]?.product_id?.product_type?.product_category
                          ?.max_temperature
                      )
                    }}
                  </div>

                  <NoteInput
                    v-else-if="field.name == 'note'"
                    :id="id"
                    :index="index"
                    :field="field"
                    :value="computedLoads[index]?.[field.name]"
                    @input="
                      handleUpdateFormValues({
                        key: field.name,
                        index,
                        value: $event,
                      })
                    "
                  />
                </b-td>
              </b-tr>
            </template>
          </b-tbody>
        </b-table-simple>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { EventBus } from '../lib/event-bus.js'

import McCheckbox from '@/components/McCheckbox'
import McButton from '@/components/McButton'
import ProductValueHelp from './wrapper/ProductValueHelp'
import QuantityInput from './wrapper/QuantityInput'
import UnitSelectResourceful from './wrapper/UnitSelectResourceful'
import NoteInput from './wrapper/NoteInput'

import { getLoadFields } from '../lib/fields/load-fields.js'
import get from 'lodash/get'

export default {
  components: {
    McCheckbox,
    McButton,
    ProductValueHelp,
    QuantityInput,
    UnitSelectResourceful,
    NoteInput,
  },
  props: {
    id: {
      type: Number,
    },
  },
  data() {
    return {
      selectedAll: false,
      selectedIdx: {},
      items: [],
    }
  },
  computed: {
    ...mapState('delivery', ['bulkLoadValues', 'bulkLoadObjects']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    fields() {
      return this.filterFieldsByScopes(getLoadFields((key, params) => this.$t(key, params)))
    },
    computedObject() {
      return this.bulkLoadObjects[this.id] || []
    },
    computedLoads() {
      return this.bulkLoadValues[this.id] || []
    },
    computedFields() {
      return [
        {
          name: 'checkbox',
          label: '',
          thStyle: 'width: 20px',
          tdStyle: 'width: 20px',
        },
        ...this.fields,
      ]
    },
    selectedIdxLength() {
      const selectedIdx = Object.keys(this.selectedIdx)
      return selectedIdx.filter((item) => this.selectedIdx[item] == true).length
    },
  },
  watch: {
    computedLoads: {
      immediate: true,
      handler(loads = []) {
        this.items = loads.map(() => ({}))

        const selectedIdx = {}

        Object.keys(this.selectedIdx).forEach((key) => {
          if (loads[key] !== undefined && this.selectedIdx[key]) {
            selectedIdx[key] = true
          }
        })

        this.selectedIdx = selectedIdx
        this.selectedAll = loads.length > 0 && Object.keys(selectedIdx).length === loads.length
      },
    },
  },
  methods: {
    handleUnselectAll() {
      this.selectedIdx = {}
      this.selectedAll = false
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIdx = Object.fromEntries(this.items.map((item, index) => [index, true]))
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleAdd() {
      let newLoads = [{ quantity: 1 }]

      if (this.computedLoads.length > 0) {
        newLoads = [...this.computedLoads, { quantity: 1 }]
      }

      this.items.push({})
      this.$store.dispatch('delivery/setBulkLoadValues', { key: this.id, value: newLoads })
    },
    handleDelete() {
      const newItems = []
      const loadStates = []

      this.items.forEach((item, index) => {
        if (this.selectedIdx[index]) return

        newItems.push(item)
        loadStates.push(this.computedLoads[index])
      })

      this.items = newItems
      this.selectedIdx = {}
      this.selectedAll = false
      this.$store.dispatch('delivery/setBulkLoadValues', { key: this.id, value: loadStates })
    },
    handleUpdateFormValues({ key, index, value }) {
      if (value == null) {
        this.handleUpdateFullObjects({ key, index, value: {} })
      }

      const newLoads = [...this.computedLoads]

      newLoads[index] = {
        ...newLoads[index],
        [key]: value,
      }

      this.$store.dispatch('delivery/setBulkLoadValues', { key: this.id, value: newLoads })
      EventBus.$emit('remove-error', `${key}-${this.id}-${index}`)
    },
    handleUpdateFullObjects({ key, index, value }) {
      const newObjects = [...this.computedObject]

      newObjects[index] = {
        ...newObjects[index],
        [key]: { ...value },
      }

      this.$store.dispatch('delivery/setBulkLoadObjects', { key: this.id, value: newObjects })
    },
    getProductType({ index, key }) {
      return get(this.computedObject[index]?.product_id, key) || '-'
    },
    getWeight({ index }) {
      const qty = this.computedLoads[index]?.quantity
      return get(this.computedObject[index]?.uom, 'weight') * qty || '-'
    },
    getVolume({ index }) {
      const qty = this.computedLoads[index]?.quantity
      return get(this.computedObject[index]?.uom, 'volume') * qty || '-'
    },
    filterFieldsByScopes(fields) {
      return fields.filter((field) => {
        if (field.scopes) {
          return this.hasAccessAll(field.scopes)
        }
        return true
      }).map((field) => {
        if (field.tableFields) {
          return {
            ...field,
            tableFields: this.filterFieldsByScopes(field.tableFields)
          }
        }
        return field
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.loads-table {
  margin: 8px 0;
  span {
    font-size: 16px;
    color: $mep-color-text-400;
  }

  .table-responsive {
    overflow-x: auto;

    .editable-table {
      width: 100%;
      min-width: 600px;
      table-layout: fixed;

      .checkbox-cell {
        text-align: center;
        padding-right: 8px;

        display: flex;
        align-items: center;
        justify-content: center;
      }

      .td__overflow-ellipsis {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  &__title-container {
    display: flex;
    justify-content: space-between;
    margin: 8px 0;
  }

  &__actions-container {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }
}
</style>
